import { memo, useState, useMemo, useEffect, useCallback, useRef } from "react";
import { ReactSVG } from "react-svg";
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { Menu, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import InterventionCreate from "../service-steps/medication/interventionCreate";
import MedicationCreate from "../service-steps/clinical-assessment/MedicationCreate";
import {
  searchInterventions,
  getInterventionById,
  updateInterventionAssessment,
  updateInterventionStatus,
} from "../../../../redux/modules/intervention/actions";
import {
  setDrugStatus,
  setPrescribedRxEnabled,
  setSortBy,
} from "../../../../redux/modules/medications/actions";
import { formatDateToDD_MMM_YYYY, capitalizeFirstLetter } from "../../../../helpers/functions";
import {
  InterventionStatus,
  getInterventionStatusLabel,
  getInterventionStatusColor,
  isActiveInterventionStatus,
  isWaitingProviderInterventionStatus,
  readAcknowledgedInterventionIds,
  INTERVENTION_ACKNOWLEDGED_EVENT,
} from "../../Patient-Menu/Interventions/data";
import { InterventionStepsEnum } from "../../text/data";
import Medications from "../medications/medications";
import ConfirmationModalNew from "../../../common/ConfirmationModalNew";
import CustomInput from "../../../common/CustomInput";

const urgencyColors = {
  Medium: "bg-yellow-50 text-yellow-800 border-yellow-100",
  Low: "bg-green-50 text-green-800 border-green-100",
  High: "bg-red-50 text-red-800 border-red-100",
};

const INTERVENTION_CARD_CLASS =
  "w-full justify-center border-[3px] gap-[13px] rounded-lg p-2 flex flex-col transition-all duration-150 cursor-pointer hover:shadow-md font-inter";
const STATUS_ROW_CLASS =
  "flex w-full justify-between items-center rounded-[5px] border-[1px] px-[18px] py-[9px]";
const MENU_BUTTON_CLASS =
  "block w-full text-left p-[10px] border-b border-b-gray-20 hover:bg-gray-50";
/** Root + nested flyouts use the same width so Resolved / Sort By panels align with the main filter menu. */
const INTERVENTION_FILTER_MENU_WIDTH = "min-w-[200px] w-[200px]";
/** Medications filter: root + flyouts share width (Category row needs ~280px for its label). */
const MEDICATION_FILTER_MENU_WIDTH = "min-w-[240px] w-[240px]";

const drugTherapyProblemOptions = [
  { value: "therapeuticDuplication", label: "Therapeutic Duplication" },
  { value: "requiresDrug", label: "Requires Drug" },
  { value: "subOptimalResponse", label: "Sub-Optimal Response" },
  { value: "dosagetooLow", label: "Dosage too Low" },
  { value: "adverseDrugReaction", label: "Adverse Drug Reaction" },
  { value: "dangerouslyHighDose", label: "Dangerously High Dose" },
  { value: "nonCompliance", label: "Non-Compliance" },
  { value: "prescriptionChanged", label: "Prescription Change" },
];

const interventionDateRangeOptions = [
  { value: "last4Weeks", label: "Last 4 weeks" },
  { value: "last3Months", label: "Last 3 months" },
  { value: "last6Months", label: "Last 6 months" },
  { value: "last12Months", label: "Last 12 months" },
  { value: "custom", label: "Custom Range", disabled: true },
];

const interventionTypeFilterOptions = [
  { value: "all", label: "All Types" },
  { value: "PharmaceuticalOpinion", label: "Pharmaceutical Opinion" },
  { value: "prescriptionAdaptation", label: "Prescription Adaptation" },
];

const clinicalServiceTypeFilterOptions = [
  { value: "all", label: "All" },
  { value: "Minor Ailment", label: "Minor Ailment" },
  { value: "Medication Review", label: "Medication Review" },
];

const clincalServiceFilterOptions = [
  { value: "clinicalServices", label: "Clinical Services" },
  { value: "patientChart", label: "Patient Chart" },
];

const DEFAULT_FILTERS = {
  selectedFilter: localStorage.getItem("interventionFilterStatus") || "all",
  selectedSort: localStorage.getItem("interventionSortOrder") || "newest",
  dateRange: localStorage.getItem("interventionDateRange") || "last4Weeks",
  selectedInterventionType: localStorage.getItem("interventionTypeFilter") || "all",
  selectedClinicalServiceScope:
    localStorage.getItem("interventionClinicalServiceScope") ||
    "clinicalServices",
  selectedClinicalServiceType:
    localStorage.getItem("clinicalServiceTypeFilter") || "all",
};

const syncFiltersToStorage = (filters) => {
  localStorage.setItem("interventionFilterStatus", filters.selectedFilter);
  localStorage.setItem("interventionSortOrder", filters.selectedSort);
  localStorage.setItem("interventionDateRange", filters.dateRange);
  localStorage.setItem("interventionTypeFilter", filters.selectedInterventionType);
  localStorage.setItem(
    "interventionClinicalServiceScope",
    filters.selectedClinicalServiceScope
  );
  localStorage.setItem(
    "clinicalServiceTypeFilter",
    filters.selectedClinicalServiceType
  );
};

const parseSafeDate = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const getInterventionDetails = (intervention) =>
  intervention?.interventionAssessment?.interventionAssessmentForm
    ?.interventionDetails || {};

const getMedicationNameFallback = (interventionDetails) =>
  interventionDetails?.medicationName &&
  interventionDetails.medicationName !== "[Name of Drug]"
    ? interventionDetails.medicationName
    : "Name of Drug";

const DIRTY_CHECK_IGNORED_KEYS = new Set([
  "currentStep",
  "lastModifiedDate",
  "lastModifiedBy",
  "createdDate",
  "createdBy",
]);
const normalizeForDirtyCheck = (value) =>
  Array.isArray(value)
    ? value.map(normalizeForDirtyCheck)
    : value && typeof value === "object"
      ? Object.keys(value)
          .sort()
          .reduce((acc, key) => {
            if (!DIRTY_CHECK_IGNORED_KEYS.has(key)) {
              acc[key] = normalizeForDirtyCheck(value[key]);
            }
            return acc;
          }, {})
      : value;
const createDirtyCheckSnapshot = (form) =>
  form ? JSON.stringify(normalizeForDirtyCheck(form)) : null;

const getAssessmentForm = (intervention) =>
  intervention?.interventionAssessment?.interventionAssessmentForm || null;

const createDebouncer = (delay = 300) => {
  let timeoutId;
  return (cb) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(cb, delay);
    return () => clearTimeout(timeoutId);
  };
};

const getStatusLabel = (status) => getInterventionStatusLabel(status);

const useInterventionsFilter = ({
  interventions,
  filters,
  dateRange,
}) =>
  useMemo(() => {
    let filtered = interventions;
    const {
      selectedFilter,
      selectedSort,
      selectedInterventionType,
      selectedClinicalServiceScope,
      selectedClinicalServiceType,
    } = filters;

    const byStatus = (intervention) => {
      switch (selectedFilter) {
        case "active":
          return isActiveInterventionStatus(intervention.status);
        case "waitingProviderResponse":
          return isWaitingProviderInterventionStatus(intervention.status);
        case "pharmacistFollowUp":
          return (
            intervention?.interventionType === "PharmacistFollowUp" ||
            intervention?.interventionType === "pharmacistFollowUp"
          );
        case "completed":
          return intervention.status === InterventionStatus.Completed;
        case "deleted":
        case "cancelled":
          return intervention.status === InterventionStatus.Cancelled;
        default:
          return true;
      }
    };

    if (selectedClinicalServiceScope !== "patientChart" && selectedFilter !== "all") {
      filtered = filtered.filter(byStatus);
    }
    if (selectedInterventionType !== "all") {
      filtered = filtered.filter(
        (intervention) =>
          intervention.interventionType === selectedInterventionType
      );
    }
    if (selectedClinicalServiceScope === "patientChart") {
      filtered = filtered.filter(
        (intervention) => {
          const serviceType = intervention?.clinicalServiceType;
          return !serviceType || String(serviceType).trim() === "" || serviceType === "Patient Chart";
        }
      );
    } else if (selectedClinicalServiceType !== "all") {
      filtered = filtered.filter(
        (intervention) =>
          intervention.clinicalServiceType === selectedClinicalServiceType
      );
    }
    if (dateRange !== "all") {
      const now = new Date();
      let startDate = null;
      switch (dateRange) {
        case "last4Weeks":
          startDate = new Date(now.getTime() - 4 * 7 * 24 * 60 * 60 * 1000);
          break;
        case "last3Months":
          startDate = new Date(now);
          startDate.setMonth(startDate.getMonth() - 3);
          break;
        case "last6Months":
          startDate = new Date(now);
          startDate.setMonth(startDate.getMonth() - 6);
          break;
        case "last12Months":
          startDate = new Date(now);
          startDate.setMonth(startDate.getMonth() - 12);
          break;
        default:
          break;
      }
      if (startDate) {
        filtered = filtered.filter((intervention) => {
          const interventionDate = parseSafeDate(
            intervention.createdDate || intervention.lastModifiedDate
          );
          return interventionDate ? interventionDate >= startDate : false;
        });
      }
    }
    if (selectedSort === "default") return filtered;
    return filtered
      .map((item, index) => ({ item, index }))
      .sort((a, b) => {
        const { item: itemA, index: indexA } = a;
        const { item: itemB, index: indexB } = b;
        let result = 0;
        switch (selectedSort) {
          case "alphabetic": {
            const nameA = itemA.medicationName || "";
            const nameB = itemB.medicationName || "";
            result = nameA.localeCompare(nameB);
            break;
          }
          case "newest":
            result =
              (parseSafeDate(itemB.createdDate)?.getTime() || 0) -
              (parseSafeDate(itemA.createdDate)?.getTime() || 0);
            break;
          case "oldest":
            result =
              (parseSafeDate(itemA.createdDate)?.getTime() || 0) -
              (parseSafeDate(itemB.createdDate)?.getTime() || 0);
            break;
          default:
            result = 0;
            break;
        }
        return result || indexA - indexB;
      })
      .map(({ item }) => item);
  }, [
    interventions,
    filters,
    dateRange,
  ]);

const useSelectedIntervention = ({
  interventions,
  interventionId,
  currentIntervention,
  medications,
}) => {
  const selectedIntervention = useMemo(() => {
    const currentMatchesRoute =
      currentIntervention?.id && currentIntervention.id === interventionId;
    if (currentMatchesRoute) return currentIntervention;

    const byRoute = interventions.find((it) => it.id === interventionId);
    return byRoute || currentIntervention || null;
  }, [interventions, interventionId, currentIntervention]);

  const selectedMedicationForMedications = useMemo(() => {
    const medicationId = selectedIntervention?.medicationId;
    if (!medicationId || !Array.isArray(medications)) return null;
    return medications.find((m) => m.id === medicationId) || null;
  }, [selectedIntervention?.medicationId, medications]);

  return { selectedIntervention, selectedMedicationForMedications };
};

const SidebarToggle = memo(({ showInterventions, onToggle }) => (
  <div className="-mx-2 flex justify-center items-center w-[7px] h-auto bg-gray-100">
    {!showInterventions && (
      <button
        className="rounded-full w-5 h-5 bg-teal-600 mb-[100px] border-teal-600 text-white inline-flex duration-300 items-center justify-center px-1 py-1"
        onClick={() => onToggle(true)}
      >
        <ChevronRightIcon className="w-4 h-4 text-white" />
      </button>
    )}
    {showInterventions && (
      <button
        className="rounded-full w-5 h-5 bg-teal-600 mb-[100px] border-teal-600 text-white inline-flex duration-300 items-center justify-center px-1 py-1"
        onClick={() => onToggle(false)}
      >
        <ChevronLeftIcon className="w-4 h-4 text-white" />
      </button>
    )}
  </div>
));

const InterventionFilters = memo(({
  selectedCategory,
  selectedClinicalServiceScope,
  dateRange,
  selectedInterventionType,
  selectedClinicalServiceType,
  onClinicalServiceScopeChange,
  onDateRangeChange,
  onInterventionTypeChange,
  onClinicalServiceTypeChange,
}) =>
  selectedCategory !== "interventions" ? null : (
    <div className="grid grid-cols-2 gap-1 mb-2 bg-white rounded-lg">
      <div className="relative">
        <CustomInput
          type="dropdown"
          name="interventionDateRange"
          label=""
          className="w-[85%] !py-2"
          options={interventionDateRangeOptions}
          value={dateRange}
          onChange={(selected) => onDateRangeChange(selected?.value)}
        />
      </div>
      <div className="relative">
        <CustomInput
          type="dropdown"
          name="interventionTypeFilter"
          label=""
          className="w-[115%] -ml-8 !py-2"
          options={interventionTypeFilterOptions}
          value={selectedInterventionType}
          onChange={(selected) => onInterventionTypeChange(selected?.value)}
        />
      </div>
      <div className="relative col-span-2">
        <div className="flex h-[40px] w-full overflow-visible rounded-[6px] border border-gray-100 bg-gray-10">
          <div
            className={`flex items-center justify-start ${
              selectedClinicalServiceScope === "patientChart" ? "w-full" : "w-[42%]"
            }`}
          >
            <CustomInput
              type="dropdown"
              name="interventionClinicalServiceScopeFilter"
              label=""
              className="w-full h-full !py-2 !rounded-none !border-0 font-semibold !text-teal-300 text-[16px]"
              showInputCancellation={false}
              options={clincalServiceFilterOptions}
              value={selectedClinicalServiceScope || "clinicalServices"}
              onChange={(selected) => onClinicalServiceScopeChange(selected?.value)}
            />
          </div>
          {selectedClinicalServiceScope !== "patientChart" && (
            <div className="h-full w-px bg-gray-100" />
          )}
          <div className="relative flex-1 bg-gray-20 pl-3">
            {selectedClinicalServiceScope === "patientChart" ? null : (
              <CustomInput
                type="dropdown"
                name="interventionClinicalServiceTypeFilter"
                label=""
                className="w-full h-full !py-2 !rounded-none !border-0 bg-gray-20 text-[16px]"
                showInputCancellation={false}
                options={clinicalServiceTypeFilterOptions}
                value={selectedClinicalServiceType || "all"}
                onChange={(selected) =>
                  onClinicalServiceTypeChange(selected?.value)
                }
              />
            )}
          </div>
        </div>
      </div>
    </div>
  ));

const MedicationSection = memo(({ selectedMedication }) => (
  <div className="flex flex-col">
    <Medications
      isAMR={false}
      showPrescribedRx={false}
      showMedication={true}
      selectedMedication={selectedMedication}
      isIntervention={true}
    />
  </div>
));

const InterventionListItems = memo(({
  items,
  interventionId,
  acknowledgedInterventionIds,
  onInterventionClick,
  onDelete,
  getClinicalServiceTypeDisplay,
  getMedicationDisplayName,
  getDrugTherapyProblemDisplay,
}) => (
  <div className="flex flex-col gap-[21px] max-h-[600px] overflow-y-auto interventions-scrollable">
    {!items?.length && (
      <div className="w-full max-w-[460px] border border-gray-100 rounded-lg p-4 text-sm text-gray-500">
        No interventions found.
      </div>
    )}
    {items?.map((item) => {
      const isSelected = interventionId === item.id;
      const interventionDetails = getInterventionDetails(item);
      const urgencyLevel = capitalizeFirstLetter(interventionDetails?.urgencyLevel || "Low");
      return (
        <div
          key={item.id}
          className={`${INTERVENTION_CARD_CLASS} ${
            isSelected ? "border-teal-300" : "border-gray-100"
          }`}
          onClick={onInterventionClick(item)}
        >
          <div
            className={`${STATUS_ROW_CLASS} ${getInterventionStatusColor(
              item.status
            )}`}
          >
            <div className="flex items-center gap-2">
              <span className="font-bold text-gray-800 text-[16px]">
                {getStatusLabel(item.status)}
              </span>
              {acknowledgedInterventionIds?.has(item.id) && (
                <ReactSVG
                  src="/icons/medication-card-complete.svg"
                  beforeInjection={(svg) => {
                    // className lands on the wrapper; the icon carries its own
                    // 61x50 size, so constrain the svg itself (viewBox scales it).
                    svg.setAttribute("class", "h-5 w-6");
                  }}
                  className="flex items-center"
                />
              )}
            </div>
            <div className="flex items-center gap-[10px]">
              <span className="text-md-body font-medium text-gray-800">
                {item.createdDate ? formatDateToDD_MMM_YYYY(item.createdDate) : ""}
              </span>
              {item.status === InterventionStatus.Active && (
                <button
                  className="p-1 hover:bg-gray-100 rounded"
                  onClick={onDelete(item)}
                  title="Delete"
                >
                  <TrashIcon className="w-4 h-4 text-red-400" />
                </button>
              )}
            </div>
          </div>
          <div className="flex-1 flex flex-col gap-[9px] px-[18px] bg-white">
            <div className="text-sm text-gray-800">
              <span className="font-normal">Clinical Service:</span>{" "}
              <span className="font-semibold">
                {getClinicalServiceTypeDisplay(item)}
              </span>
            </div>
            <div className="text-sm text-gray-800">
              <span className="font-normal">Drug Name:</span>{" "}
              <span className="font-semibold">{getMedicationDisplayName(item)}</span>
            </div>
            <div className="text-sm text-gray-800">
              <span className="font-normal">Drug Therapy Problem:</span>{" "}
              <span className="font-semibold">
                {getDrugTherapyProblemDisplay(interventionDetails?.drugTherapyProblem)}
              </span>
            </div>
            <div className="text-sm flex items-center gap-2">
              <span className="font-normal text-gray-800">Urgency Level:</span>
              <span
                className={`border rounded px-2 py-0.5 text-sm font-semibold ${
                  urgencyColors[urgencyLevel] || urgencyColors.Low
                }`}
              >
                {urgencyLevel}
              </span>
            </div>
          </div>
        </div>
      );
    })}
  </div>
));

const InterventionHeader = memo(({
  selectedCategory,
  onSelectCategory,
  interventionMenu,
  medicationMenu,
  onOpenInterventionCreate,
  onOpenMedicationCreate,
}) => (
  <div className="flex flex-col gap-2 w-[460px]">
    <div className="p-0 flex flex-col gap-2 rounded-lg">
      <div
        className="flex gap-1 cursor-pointer"
        onClick={() => onSelectCategory("interventions")}
      >
        <div
          className={`min-w-0 flex-1 px-4 py-3 ${
            selectedCategory === "interventions"
              ? "bg-teal-75"
              : "bg-white hover:bg-teal-40"
          } rounded-lg border border-gray-100`}
        >
          <h3
            className={`${
              selectedCategory === "interventions"
                ? "font-semibold"
                : "font-medium"
            } text-[18px] leading-[24px] text-teal-300`}
          >
            Interventions
          </h3>
        </div>
        <div
          className={`w-[80px] shrink-0 ${
            selectedCategory === "interventions" ? "bg-teal-25" : "bg-white"
          } rounded-lg border border-gray-100 flex items-center justify-center transition-colors duration-200 ${
            selectedCategory === "interventions" ? "hover:bg-teal-40" : "opacity-50 cursor-not-allowed"
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {selectedCategory === "interventions" ? (
            interventionMenu
          ) : (
            <ReactSVG src="/icons/intervention/filter.svg" className="w-5 h-5 opacity-40" />
          )}
        </div>
        <button
          className={`w-[80px] shrink-0 ${
            selectedCategory === "interventions" ? "bg-teal-25 hover:bg-teal-40" : "bg-white opacity-50 cursor-not-allowed"
          } rounded-lg border border-gray-100 flex items-center justify-center`}
          disabled={selectedCategory !== "interventions"}
          onClick={(e) => {
            e.stopPropagation();
            onOpenInterventionCreate();
          }}
        >
          <ReactSVG src="/icons/intervention/ADD.svg" className="w-12 h-4" />
        </button>
      </div>
      <div
        className="flex gap-1 cursor-pointer"
        onClick={() => onSelectCategory("medications")}
      >
        <div
          className={`min-w-0 flex-1 px-4 py-3 ${
            selectedCategory === "medications"
              ? "bg-teal-75"
              : "bg-white hover:bg-teal-40"
          } rounded-lg border border-gray-100`}
        >
          <h3
            className={`${
              selectedCategory === "medications" ? "font-semibold" : "font-medium"
            } text-[18px] leading-[24px] text-teal-300`}
          >
            Medications
          </h3>
        </div>
        <div
          className={`w-[80px] shrink-0 ${
            selectedCategory === "medications" ? "bg-teal-25" : "bg-white"
          } rounded-lg border border-gray-100 flex items-center justify-center transition-colors duration-200 ${
            selectedCategory === "medications" ? "hover:bg-teal-40" : "opacity-50 cursor-not-allowed"
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {selectedCategory === "medications" ? (
            medicationMenu
          ) : (
            <ReactSVG src="/icons/intervention/filter.svg" className="w-5 h-5 opacity-40" />
          )}
        </div>
        <button
          className={`w-[80px] shrink-0 ${
            selectedCategory === "medications" ? "bg-teal-25 hover:bg-teal-40" : "bg-white opacity-50 cursor-not-allowed"
          } rounded-lg border border-gray-100 flex items-center justify-center`}
          disabled={selectedCategory !== "medications"}
          onClick={(e) => {
            e.stopPropagation();
            onOpenMedicationCreate();
          }}
        >
          <ReactSVG src="/icons/intervention/ADD.svg" className="w-12 h-4" />
        </button>
      </div>
    </div>
  </div>
));

const InterventionList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { id: patientId, interventionId } = useParams();

  // Get data directly from Redux intervention state - only re-render when interventions array changes
  const interventionState = useSelector((state) => state.intervention);
  const { patient } = useSelector((state) => state.patients);
  const { medications } = useSelector((state) => state.clinicalService);
  const { drugStatus, sortBy, prescribedRxEnabled } = useSelector(
    (state) => state.medications
  );

  // Memoize interventions to prevent re-renders when only currentStep changes
  const interventions = useMemo(() => {
    return interventionState.interventions || [];
  }, [interventionState.interventions]);

  const [showInterventions, setShowInterventions] = useState(true);
  const [showInterventionCreate, setShowInterventionCreate] = useState(false);
  const [showMedicationSlider, setShowMedicationSlider] = useState(false);
  const [clickedMedication, setClickedMedication] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(
    location?.state?.selectedCategory || "interventions"
  );
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [interventionToDelete, setInterventionToDelete] = useState(null);

  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  const updateFilters = useCallback((updater) => {
    setFilters((prev) => {
      const next = updater(prev);
      syncFiltersToStorage(next);
      return next;
    });
  }, []);

  const handleFilterChange = useCallback(
    (filter) => {
      if (selectedCategory !== "interventions") return;
      setSelectedCategory("interventions");
      updateFilters((prev) => ({ ...prev, selectedFilter: filter }));
      window.dispatchEvent(
        new CustomEvent("intervention-filter-changed", {
          detail: { selectedFilter: filter },
        })
      );
    },
    [updateFilters, selectedCategory]
  );

  const handleSortChange = useCallback(
    (sort) => {
      if (selectedCategory !== "interventions") return;
      setSelectedCategory("interventions");
      updateFilters((prev) => ({ ...prev, selectedSort: sort }));
    },
    [updateFilters, selectedCategory]
  );

  const handleDateRangeChange = useCallback(
    (range) => {
      if (selectedCategory !== "interventions") return;
      setSelectedCategory("interventions");
      updateFilters((prev) => ({ ...prev, dateRange: range }));
    },
    [updateFilters, selectedCategory]
  );

  const handleInterventionTypeChange = useCallback(
    (type) => {
      if (selectedCategory !== "interventions") return;
      setSelectedCategory("interventions");
      updateFilters((prev) => ({ ...prev, selectedInterventionType: type }));
    },
    [updateFilters, selectedCategory]
  );

  const handleClinicalServiceTypeChange = useCallback(
    (type) => {
      if (selectedCategory !== "interventions") return;
      setSelectedCategory("interventions");
      updateFilters((prev) => ({ ...prev, selectedClinicalServiceType: type }));
    },
    [updateFilters, selectedCategory]
  );
  const handleClinicalServiceScopeChange = useCallback(
    (scope) => {
      if (selectedCategory !== "interventions") return;
      setSelectedCategory("interventions");
      updateFilters((prev) => ({
        ...prev,
        selectedClinicalServiceScope: scope,
      }));
    },
    [updateFilters, selectedCategory]
  );

  const handleMedicationFilterChange = useCallback(
    (filter) => {
      if (selectedCategory !== "medications") return;
      setSelectedCategory("medications");
      dispatch(setDrugStatus(filter));
    },
    [dispatch, selectedCategory]
  );

  const handleMedicationSortChange = useCallback(
    (sort) => {
      if (selectedCategory !== "medications") return;
      setSelectedCategory("medications");
      dispatch(setSortBy(sort));
    },
    [dispatch, selectedCategory]
  );

  const fetchInterventions = useCallback(() => {
    if (!patientId) return;
    const {
      selectedFilter,
      selectedInterventionType,
      selectedClinicalServiceScope,
      selectedClinicalServiceType,
    } = filters;
    dispatch(
      searchInterventions(patientId, {
        status:
          selectedClinicalServiceScope === "patientChart"
            ? null
            : selectedFilter === "all"
              ? null
              : selectedFilter,
        interventionType:
          selectedInterventionType === "all" ? null : selectedInterventionType,
        clinicalServiceType:
          selectedClinicalServiceScope === "patientChart"
            ? null
            : selectedClinicalServiceType === "all"
              ? null
              : selectedClinicalServiceType,
        pageNumber: 1,
        pageSize: 100,
      })
    );
  }, [dispatch, patientId, filters]);

  useEffect(() => {
    if (!patientId) return;
    const debounce = createDebouncer(300);
    return debounce(() => {
      fetchInterventions();
    });
  }, [fetchInterventions, patientId]);

  const selectedCategoryFromNavigation = location?.state?.selectedCategory;

  // Filter menu component
  const getFilterMenu = useCallback(() => {
    const { selectedFilter, selectedSort } = filters;
    return (
      <Menu as="div" className="relative inline-block text-left mt-2">
        <div className="flex items-center">
          <Menu.Button className="p-1 hover:bg-teal-40 rounded transition-colors duration-200">
            <ReactSVG
              src="/icons/intervention/filter.svg"
              className="w-5 h-5"
            />
          </Menu.Button>
        </div>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform translate-y-2 opacity-0 scale-95"
          enterTo="transform translate-y-0 opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform translate-y-0 opacity-100 scale-100"
          leaveTo="transform translate-y-2 opacity-0 scale-95"
        >
          <Menu.Items
            className={`${INTERVENTION_FILTER_MENU_WIDTH} text-sm font-normal absolute z-[9999] mt-2 left-0 origin-top-left bg-white divide-y divide-gray-20 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none`}
          >
            <Menu.Item>
              <button
                className={`${MENU_BUTTON_CLASS} ${
                  selectedFilter === "all" && "bg-teal-25"
                } hover:bg-gray-50`}
                onClick={() => handleFilterChange("all")}
              >
                All
              </button>
            </Menu.Item>
            <Menu.Item>
              <button
                className={`${MENU_BUTTON_CLASS} ${
                  selectedFilter === "active" && "bg-teal-25"
                } hover:bg-gray-50`}
                onClick={() => handleFilterChange("active")}
              >
                Active
              </button>
            </Menu.Item>
            <Menu.Item>
              <button
                className={`${MENU_BUTTON_CLASS} ${
                  selectedFilter === "waitingProviderResponse" && "bg-teal-25"
                } hover:bg-gray-50`}
                onClick={() => handleFilterChange("waitingProviderResponse")}
              >
                Waiting Provider Response
              </button>
            </Menu.Item>
            <Menu.Item>
              <button
                className={`${MENU_BUTTON_CLASS} ${
                  selectedFilter === "pharmacistFollowUp" && "bg-teal-25"
                } hover:bg-gray-50`}
                onClick={() => handleFilterChange("pharmacistFollowUp")}
              >
                Pharmacist Follow-Up
              </button>
            </Menu.Item>
            <Menu.Item className="relative">
              <Menu
                as="div"
                className="block w-full p-[10px] border-b border-b-gray-20 hover:bg-gray-50"
              >
                <div>
                  <Menu.Button className="w-full flex flex-row items-center justify-between text-left">
                    <span>Resolved</span>
                    <ChevronRightIcon className="w-4 h-4" />
                  </Menu.Button>
                </div>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform translate-y-2 opacity-0 scale-95"
                  enterTo="transform translate-y-0 opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform translate-y-0 opacity-100 scale-100"
                  leaveTo="transform translate-y-2 opacity-0 scale-95"
                >
                  <Menu.Items
                    className={`${INTERVENTION_FILTER_MENU_WIDTH} absolute z-[9999] top-0 right-0 translate-x-full bg-white divide-y divide-gray-20 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none`}
                  >
                    <Menu.Item>
                      <button
                        className={`${MENU_BUTTON_CLASS} ${
                          selectedFilter === "completed" && "bg-teal-25"
                        }`}
                        onClick={() => handleFilterChange("completed")}
                      >
                        Completed
                      </button>
                    </Menu.Item>
                    <Menu.Item>
                      <button
                        className={`${MENU_BUTTON_CLASS} ${
                          selectedFilter === "cancelled" && "bg-teal-25"
                        }`}
                        onClick={() => handleFilterChange("cancelled")}
                      >
                        Deleted
                      </button>
                    </Menu.Item>
                  </Menu.Items>
                </Transition>
              </Menu>
            </Menu.Item>
            <Menu.Item className="relative">
              <Menu
                as="div"
                className="block w-full p-[10px] border-b border-b-gray-20 hover:bg-gray-50"
              >
                <div>
                  <Menu.Button className="w-full flex flex-row items-center justify-between text-left">
                    <span>Sort By</span>
                    <ChevronRightIcon className="w-4 h-4" />
                  </Menu.Button>
                </div>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform translate-y-2 opacity-0 scale-95"
                  enterTo="transform translate-y-0 opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform translate-y-0 opacity-100 scale-100"
                  leaveTo="transform translate-y-2 opacity-0 scale-95"
                >
                  <Menu.Items
                    className={`${INTERVENTION_FILTER_MENU_WIDTH} absolute z-[9999] top-0 right-0 translate-x-full bg-white divide-y divide-gray-20 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none`}
                  >
                    <Menu.Item>
                      <button
                        className={`${MENU_BUTTON_CLASS} ${
                          selectedSort === "alphabetic" && "bg-teal-25"
                        }`}
                        onClick={() => handleSortChange("alphabetic")}
                      >
                        Alphabetical
                      </button>
                    </Menu.Item>
                    <Menu.Item>
                      <button
                        className={`${MENU_BUTTON_CLASS} ${
                          selectedSort === "newest" && "bg-teal-25"
                        }`}
                        onClick={() => handleSortChange("newest")}
                      >
                        Newest on top
                      </button>
                    </Menu.Item>
                    <Menu.Item>
                      <button
                        className={`${MENU_BUTTON_CLASS} ${
                          selectedSort === "oldest" && "bg-teal-25"
                        }`}
                        onClick={() => handleSortChange("oldest")}
                      >
                        Oldest on top
                      </button>
                    </Menu.Item>
                  </Menu.Items>
                </Transition>
              </Menu>
            </Menu.Item>
          </Menu.Items>
        </Transition>
      </Menu>
    );
  }, [filters, handleFilterChange, handleSortChange]);

  const getMedicationFilterMenu = useCallback(() => {
    return (
      <Menu as="div" className="relative inline-block text-left mt-2">
        <div className="flex items-center">
          <Menu.Button className="p-1 hover:bg-teal-40 rounded transition-colors duration-200">
            <ReactSVG
              src="/icons/intervention/filter.svg"
              className="w-5 h-5"
            />
          </Menu.Button>
        </div>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform translate-y-2 opacity-0 scale-95"
          enterTo="transform translate-y-0 opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform translate-y-0 opacity-100 scale-100"
          leaveTo="transform translate-y-2 opacity-0 scale-95"
        >
          <Menu.Items
            className={`${MEDICATION_FILTER_MENU_WIDTH} text-sm font-normal absolute z-[9999] mt-2 left-0 origin-top-left bg-white divide-y divide-gray-20 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none`}
          >
            <Menu.Item>
              <button
                className={`${MENU_BUTTON_CLASS} ${
                  drugStatus === "all" ? "bg-teal-25" : ""
                } hover:bg-gray-50`}
                onClick={() => handleMedicationFilterChange("all")}
              >
                All
              </button>
            </Menu.Item>
            <Menu.Item>
              <button
                className={`${MENU_BUTTON_CLASS} ${
                  drugStatus === "active" ? "bg-teal-25" : ""
                } hover:bg-gray-50`}
                onClick={() => handleMedicationFilterChange("active")}
              >
                Active
              </button>
            </Menu.Item>
            <Menu.Item>
              <button
                className={`${MENU_BUTTON_CLASS} ${
                  drugStatus === "inactive" ? "bg-teal-25" : ""
                } hover:bg-gray-50`}
                onClick={() => handleMedicationFilterChange("inactive")}
              >
                Inactive
              </button>
            </Menu.Item>
            <Menu.Item className="relative">
              <Menu
                as="div"
                className="block w-full p-[10px] border-b border-b-gray-20 hover:bg-gray-50"
              >
                <div>
                  <Menu.Button className="w-full flex flex-row items-center justify-between text-left">
                    <span>Category Type</span>
                    <ChevronRightIcon className="w-4 h-4" />
                  </Menu.Button>
                </div>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform translate-y-2 opacity-0 scale-95"
                  enterTo="transform translate-y-0 opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform translate-y-0 opacity-100 scale-100"
                  leaveTo="transform translate-y-2 opacity-0 scale-95"
                >
                  <Menu.Items
                    className={`${MEDICATION_FILTER_MENU_WIDTH} absolute z-[9999] top-0 right-0 translate-x-full bg-white divide-y divide-gray-20 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none`}
                  >
                    <Menu.Item>
                      <button
                        className={`${MENU_BUTTON_CLASS} ${
                          prescribedRxEnabled ? "bg-teal-25" : ""
                        }`}
                        onClick={() => {
                          setSelectedCategory("medications");
                          dispatch(setPrescribedRxEnabled(!prescribedRxEnabled));
                        }}
                      >
                        Prescribed - Pending Dispense
                      </button>
                    </Menu.Item>
                  </Menu.Items>
                </Transition>
              </Menu>
            </Menu.Item>
            <Menu.Item className="relative">
              <Menu
                as="div"
                className="block w-full p-[10px] border-b border-b-gray-20 hover:bg-gray-50"
              >
                <div>
                  <Menu.Button className="w-full flex flex-row items-center justify-between text-left">
                    <span>Sort Order</span>
                    <ChevronRightIcon className="w-4 h-4" />
                  </Menu.Button>
                </div>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform translate-y-2 opacity-0 scale-95"
                  enterTo="transform translate-y-0 opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform translate-y-0 opacity-100 scale-100"
                  leaveTo="transform translate-y-2 opacity-0 scale-95"
                >
                  <Menu.Items
                    className={`${MEDICATION_FILTER_MENU_WIDTH} absolute z-[9999] top-0 left-0 translate-x-full bg-white divide-y divide-gray-20 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none`}
                  >
                    <Menu.Item>
                      <button
                        className={`${MENU_BUTTON_CLASS} ${
                          sortBy === "alphabetic" ? "bg-teal-25" : ""
                        }`}
                        onClick={() => handleMedicationSortChange("alphabetic")}
                      >
                        Alphabetical
                      </button>
                    </Menu.Item>
                    <Menu.Item>
                      <button
                        className={`${MENU_BUTTON_CLASS} ${
                          sortBy === "newest" ? "bg-teal-25" : ""
                        }`}
                        onClick={() => handleMedicationSortChange("newest")}
                      >
                        Newest on top
                      </button>
                    </Menu.Item>
                    <Menu.Item>
                      <button
                        className={`${MENU_BUTTON_CLASS} ${
                          sortBy === "oldest" ? "bg-teal-25" : ""
                        }`}
                        onClick={() => handleMedicationSortChange("oldest")}
                      >
                        Oldest on top
                      </button>
                    </Menu.Item>
                  </Menu.Items>
                </Transition>
              </Menu>
            </Menu.Item>
          </Menu.Items>
        </Transition>
      </Menu>
    );
  }, [
    drugStatus,
    sortBy,
    prescribedRxEnabled,
    handleMedicationFilterChange,
    handleMedicationSortChange,
    dispatch,
  ]);

  const filteredAndSortedInterventions = useInterventionsFilter({
    interventions,
    filters,
    dateRange: filters.dateRange,
  });

  const { selectedIntervention, selectedMedicationForMedications } =
    useSelectedIntervention({
      interventions,
      interventionId,
      currentIntervention: interventionState.currentIntervention,
      medications,
    });
  const routeInterventionFromList = useMemo(
    () => interventions.find((it) => it.id === interventionId) || null,
    [interventions, interventionId]
  );
  const routeInterventionFromCurrent = useMemo(() => {
    const current = interventionState.currentIntervention;
    return current?.id === interventionId ? current : null;
  }, [interventionState.currentIntervention, interventionId]);
  const dirtyCheckCandidates = useMemo(
    () => [
      selectedIntervention,
      routeInterventionFromCurrent,
      routeInterventionFromList,
    ],
    [selectedIntervention, routeInterventionFromCurrent, routeInterventionFromList]
  );
  const initialFormSnapshotRef = useRef(null);
  const snapshotInterventionIdRef = useRef(null);

  useEffect(() => {
    const form = getAssessmentForm(dirtyCheckCandidates.find(Boolean));
    const selectedId = selectedIntervention?.id;

    if (!selectedId || !form) {
      initialFormSnapshotRef.current = null;
      snapshotInterventionIdRef.current = null;
      return;
    }

    if (snapshotInterventionIdRef.current !== selectedId) {
      const snapshot = createDirtyCheckSnapshot(form);
      initialFormSnapshotRef.current = snapshot;
      snapshotInterventionIdRef.current = selectedId;
      return;
    }
  }, [selectedIntervention?.id, dirtyCheckCandidates]);

  const isFormDirtyNow = useCallback(() => {
    if (!initialFormSnapshotRef.current) {
      return false;
    }

    const initial = initialFormSnapshotRef.current;
    return dirtyCheckCandidates.some((candidate) => {
      const currentSnapshot = createDirtyCheckSnapshot(getAssessmentForm(candidate));
      return currentSnapshot !== initial;
    });
  }, [dirtyCheckCandidates]);

  const onToggleInterventions = useCallback((value) => {
    setShowInterventions(value);
  }, []);

  const onSelectCategory = useCallback((category) => {
    setSelectedCategory(category);
  }, []);

  // Keep one effect for sidebar focus events.
  useEffect(() => {
    const handler = (e) => {
      const d = e?.detail;
      if (!d?.interventionId || d.interventionId !== interventionId) return;
      if (patientId && d.patientId && d.patientId !== patientId) return;
      setSelectedCategory("medications");
    };
    const medicationsHandler = (e) => handler(e);
    const interventionsHandler = (e) => {
      const d = e?.detail;
      if (!d?.interventionId || d.interventionId !== interventionId) return;
      if (patientId && d.patientId && d.patientId !== patientId) return;
      setSelectedCategory("interventions");
    };
    window.addEventListener(
      "intervention-sidebar-focus-medications",
      medicationsHandler
    );
    window.addEventListener(
      "intervention-sidebar-focus-interventions",
      interventionsHandler
    );
    return () => {
      window.removeEventListener(
        "intervention-sidebar-focus-medications",
        medicationsHandler
      );
      window.removeEventListener(
        "intervention-sidebar-focus-interventions",
        interventionsHandler
      );
    };
  }, [interventionId, patientId]);

  // Wrap-up acknowledgements from the Summary tab, so a signed-off intervention
  // shows a checkmark in the landscape list. Seeded from this tab's storage so
  // the mark survives navigating away and back, and a reload.
  const [acknowledgedInterventionIds, setAcknowledgedInterventionIds] = useState(
    readAcknowledgedInterventionIds
  );

  useEffect(() => {
    const handleAcknowledged = (e) => {
      const acknowledgedId = e?.detail?.interventionId;
      if (!acknowledgedId) return;
      setAcknowledgedInterventionIds((prev) =>
        prev.has(acknowledgedId) ? prev : new Set(prev).add(acknowledgedId)
      );
    };

    window.addEventListener(INTERVENTION_ACKNOWLEDGED_EVENT, handleAcknowledged);
    return () =>
      window.removeEventListener(
        INTERVENTION_ACKNOWLEDGED_EVENT,
        handleAcknowledged
      );
  }, []);

  const selectedInterventionForm =
    selectedIntervention?.interventionAssessment?.interventionAssessmentForm ||
    {};
  const selectedInterventionBilling = selectedInterventionForm?.billing || {};
  const selectedInterventionSteps = selectedIntervention?.interventionSteps || [];
  const selectedInterventionCurrentStepNumber =
    selectedInterventionForm?.currentStep || 1;
  const selectedInterventionStepIndex =
    selectedInterventionSteps.length > 0
      ? Math.min(
          Math.max(selectedInterventionCurrentStepNumber - 1, 0),
          selectedInterventionSteps.length - 1
        )
      : 0;
  const selectedInterventionCurrentStepName =
    selectedInterventionSteps?.[selectedInterventionStepIndex]?.name;
  const selectedInterventionResultAction =
    selectedInterventionBilling?.resultAction || null;

  // On Documentation step with Billing "Update Medication Record", show Medications tab.
  useEffect(() => {
    const isInterventionFocusedFilter =
      filters.selectedFilter === "active" || filters.selectedFilter === "all";
    const isSelectedInterventionActive = isActiveInterventionStatus(
      selectedIntervention?.status
    );

    // Never switch right pane to Medications for completed/cancelled cards.
    if (!isInterventionFocusedFilter || !isSelectedInterventionActive) {
      setSelectedCategory("interventions");
      return;
    }

    if (selectedCategoryFromNavigation) {
      setSelectedCategory(selectedCategoryFromNavigation);
      return;
    }

    if (!selectedIntervention) return;

    if (selectedInterventionCurrentStepName === InterventionStepsEnum.Documentation) {
      if (selectedInterventionResultAction === "UpdateMedicationRecord") {
        setSelectedCategory("medications");
      } else {
        setSelectedCategory("interventions");
      }
    } else {
      setSelectedCategory("interventions");
    }
  }, [
    filters.selectedFilter,
    selectedCategoryFromNavigation,
    selectedIntervention,
    selectedIntervention?.status,
    selectedInterventionCurrentStepName,
    selectedInterventionResultAction,
  ]);

  const hasIntervention = useMemo(() => {
    if (!interventionId) return false;
    return interventions.some((intervention) => intervention.id === interventionId);
  }, [interventions, interventionId]);

  const firstActiveIntervention = useMemo(
    () => interventions.find((intervention) => isActiveInterventionStatus(intervention.status)) || null,
    [interventions]
  );

  // Load intervention from URL on mount or when interventionId changes
  useEffect(() => {
    if (!interventionId || !patientId || hasIntervention) return;
    dispatch(getInterventionById(patientId, interventionId));
  }, [interventionId, patientId, hasIntervention, dispatch]);

  // For Active/All filters, keep right pane defaulted to an active intervention when available.
  useEffect(() => {
    const isActiveFilter = filters.selectedFilter === "active";
    const isAllFilter = filters.selectedFilter === "all";
    if ((!isActiveFilter && !isAllFilter) || !patientId || !firstActiveIntervention) {
      return;
    }

    // Active filter: enforce active selection because list itself is active-only.
    if (isActiveFilter) {
      if (interventionId && isActiveInterventionStatus(selectedIntervention?.status)) return;
      dispatch(getInterventionById(patientId, firstActiveIntervention.id));
      navigate(`/patient/${patientId}/interventions/${firstActiveIntervention.id}`);
      return;
    }

    // All filter: default to active only when nothing is selected; don't override user clicks.
    if (!interventionId) {
      dispatch(getInterventionById(patientId, firstActiveIntervention.id));
      navigate(`/patient/${patientId}/interventions/${firstActiveIntervention.id}`);
    }
  }, [
    filters.selectedFilter,
    patientId,
    firstActiveIntervention,
    interventionId,
    selectedIntervention?.status,
    dispatch,
    navigate,
  ]);

  const getDrugTherapyProblemDisplay = useCallback((problemValue) => {
    if (!problemValue) return "N/A";

    const option = drugTherapyProblemOptions.find(
      (opt) => opt.value === problemValue
    );
    return option ? option.label : problemValue;
  }, []);

  const getClinicalServiceTypeDisplay = useCallback((intervention) => {
    const serviceType = intervention?.clinicalServiceType;
    if (serviceType && String(serviceType).trim() !== "") {
      return serviceType;
    }
    return "Patient Chart";
  }, []);

  const handleInterventionClick = useCallback(
    (intervention) => async () => {
      const isSame = interventionId === intervention.id;
      const hasChanges = isFormDirtyNow();

      const shouldReset =
        isSame &&
        isActiveInterventionStatus(intervention?.status) &&
        !hasChanges;

      if (isSame && hasChanges) {
        return;
      }

      if (shouldReset) {
        await dispatch(
          updateInterventionAssessment(intervention.id, {
            currentStep: 1,
          })
        );
      }

      await dispatch(getInterventionById(patientId, intervention.id));
      navigate(`/patient/${patientId}/interventions/${intervention.id}`);
    },
    [dispatch, navigate, patientId, interventionId, isFormDirtyNow]
  );

  const handleDelete = useCallback(
    (intervention) => (e) => {
      e.stopPropagation();
      setInterventionToDelete(intervention);
      setOpenDeleteDialog(true);
    },
    []
  );

  const handleInterventionCreated = useCallback(() => {
    setShowInterventionCreate(false);
    fetchInterventions();
  }, [fetchInterventions]);

  const getMedicationDisplayName = useCallback((intervention) => {
    if (intervention?.medicationId) {
      const medication = medications?.find(
        (med) => med.id === intervention.medicationId
      );
      if (medication?.medicationName) return medication.medicationName;
      if (medication?.name) return medication.name;
    }
    return getMedicationNameFallback(getInterventionDetails(intervention));
  }, [medications]);

  const interventionMenu = useMemo(() => getFilterMenu(), [getFilterMenu]);
  const medicationMenu = useMemo(
    () => getMedicationFilterMenu(),
    [getMedicationFilterMenu]
  );

  const openInterventionCreate = useCallback(
    () => setShowInterventionCreate(true),
    []
  );
  const openMedicationCreate = useCallback(() => setShowMedicationSlider(true), []);
  const closeInterventionCreate = useCallback(
    () => setShowInterventionCreate(false),
    []
  );

  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent("intervention-filter-changed", {
        detail: { selectedFilter: filters.selectedFilter },
      })
    );
  }, [filters.selectedFilter]);

  return (
    <>
      {showInterventions && (
        <div className="w-fit p-1 min-h-[796px] max-h-[800px] flex flex-col gap-2 bg-white">
          <InterventionHeader
            selectedCategory={selectedCategory}
            onSelectCategory={onSelectCategory}
            interventionMenu={interventionMenu}
            medicationMenu={medicationMenu}
            onOpenInterventionCreate={openInterventionCreate}
            onOpenMedicationCreate={openMedicationCreate}
          />
          <InterventionFilters
            selectedCategory={selectedCategory}
            selectedClinicalServiceScope={filters.selectedClinicalServiceScope}
            dateRange={filters.dateRange}
            selectedInterventionType={filters.selectedInterventionType}
            selectedClinicalServiceType={filters.selectedClinicalServiceType}
            onClinicalServiceScopeChange={handleClinicalServiceScopeChange}
            onDateRangeChange={handleDateRangeChange}
            onInterventionTypeChange={handleInterventionTypeChange}
            onClinicalServiceTypeChange={handleClinicalServiceTypeChange}
          />

          {selectedCategory === "interventions" && (
            <InterventionListItems
              items={filteredAndSortedInterventions}
              interventionId={interventionId}
              acknowledgedInterventionIds={acknowledgedInterventionIds}
              onInterventionClick={handleInterventionClick}
              onDelete={handleDelete}
              getClinicalServiceTypeDisplay={getClinicalServiceTypeDisplay}
              getMedicationDisplayName={getMedicationDisplayName}
              getDrugTherapyProblemDisplay={getDrugTherapyProblemDisplay}
            />
          )}

          {selectedCategory === "medications" && (
            <MedicationSection
              selectedMedication={selectedMedicationForMedications}
            />
          )}
        </div>
      )}

      <SidebarToggle
        showInterventions={showInterventions}
        onToggle={onToggleInterventions}
      />

      <InterventionCreate
        open={showInterventionCreate}
        onClose={closeInterventionCreate}
        onSave={handleInterventionCreated}
        onCompleteNow={handleInterventionCreated}
        onSaveAndCompleteLater={handleInterventionCreated}
        onSaveAndComplete={handleInterventionCreated}
        goBack={closeInterventionCreate}
        onSkip={closeInterventionCreate}
        selectedMedication={null}
        patientId={patient?.id}
        clinicalServiceOrigin="MedicationReview"
      />

      {/* Medication create sidebar for adding drugs from the Interventions panel */}
      <MedicationCreate
        create={true}
        patientId={patient?.id}
        type="Medication"
        open={showMedicationSlider}
        fullMedication={clickedMedication}
        initialData={null}
        step={1}
        maxSteps={1}
        onClose={() => {
          setClickedMedication(null);
          setShowMedicationSlider(false);
        }}
        onSave={() => setShowMedicationSlider(false)}
      />

      {/* Delete confirmation dialog */}
      <ConfirmationModalNew
        open={openDeleteDialog}
        title="Confirm Delete Intervention"
        className="!min-w-[630px]"
        confirmText="Yes Proceed to Delete"
        cancelText="Cancel"
        content={() => (
          <div>
            <div className="flex font-semibold text-teal-300 text-xl mb-6">
              Are you sure you want to delete the Intervention?
            </div>
            <div className="flex font-regular text-gray-800 text-base mb-4">
              It will be moved to the Cancelled/Deleted list.
              <br />
              <br />
              Unsaved charting data will not be saved.
            </div>
          </div>
        )}
        onCross={() => setOpenDeleteDialog(false)}
        handleClose={() => setOpenDeleteDialog(false)}
        confirmAction={async () => {
          try {
            if (interventionToDelete) {
              // Update intervention status to Cancelled
              await dispatch(
                updateInterventionStatus(
                  patientId,
                  interventionToDelete.id,
                  InterventionStatus.Cancelled
                )
              );
              
              // Close dialog and reset state
              setOpenDeleteDialog(false);
              setInterventionToDelete(null);
              
              // Refresh interventions list with current filters
              fetchInterventions();
            }
          } catch (error) {
            console.error("Failed to delete intervention");
          }
        }}
      />
    </>
  );
};

export default InterventionList;
